import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Observable, of } from 'rxjs';
import type {
  AzureUserResponse,
  BaseModel,
  BaseUpdateRequest,
  BusinessUnitManagementDetailModel,
  BusinessUnitManagementModel,
  BusinessUnitModel,
  CreateBusinessUnitRequest,
  CreateOrganizationStructureRequest,
  OrgaItemBaseModel,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class ManagementService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = '/api/management/v2';

  // GET /businessUnits
  getBusinessUnit(id: string): Observable<BusinessUnitManagementDetailModel> {
    return this.http.get<BusinessUnitManagementDetailModel>(`${this.baseUrl}/businessUnits/${id}`);
  }
  // GET /businessUnits
  getBusinessUnits(): Observable<BusinessUnitManagementModel[]> {
    return this.http.get<BusinessUnitManagementModel[]>(`${this.baseUrl}/businessUnits`);
  }

  // POST /businessUnits
  createBusinessUnit(request: CreateBusinessUnitRequest): Observable<BusinessUnitModel> {
    return this.http.post<BusinessUnitModel>(`${this.baseUrl}/businessUnits`, request);
  }

  // PATCH /businessUnits
  updateBusinessUnit(request: BaseUpdateRequest): Observable<BusinessUnitModel> {
    return this.http.patch<BusinessUnitModel>(`${this.baseUrl}/businessUnits`, request);
  }

  // GET /organizations/items/:id with Query Parameters "getNested"
  getOrganizationStructure(id: string, getNested = true): Observable<OrgaItemBaseModel> {
    return this.http.get<OrgaItemBaseModel>(`${this.baseUrl}/organizations/items/${id}`, { params: { getNested } });
  }
  // POST /organizations/items
  createOrganizationStructure(request: CreateOrganizationStructureRequest): Observable<OrgaItemBaseModel[]> {
    return this.http.post<OrgaItemBaseModel[]>(`${this.baseUrl}/organizations/items`, request);
  }

  // PATCH /organizations/item
  updateOrganizationItem(request: BaseUpdateRequest): Observable<BaseModel> {
    return this.http.patch<BaseModel>(`${this.baseUrl}/organizations/item`, request);
  }

  getAzureUser(search: string) {
    const params = new URLSearchParams({
      search: search,
      limit: '10',
    }).toString();
    return this.http.get<AzureUserResponse>(`${this.baseUrl}/users?${params}`);
  }

  deleteOrganizationItem(identifier: string | undefined): Observable<unknown> {
    if (!identifier) {
      return of(null);
    }
    return this.http.delete(`${this.baseUrl}/organizations/items/${identifier}`);
  }

  /**
   *
   * @returns
   */
  getRoles() {
    return this.http.get<string[]>(`${this.baseUrl}/roles`);
  }
  /**
 * 
 * {
  "userPrincipalName": "User name",
  "businessUnitId": "d1f3a9df-f96f-4beb-aca2-05f5f912eea3",
  "roles": [
    "Administrator",
    "User"
  ]
 * @param userId 
 * @param role 
 * @returns 
 */
  assignRoleToUser(userPrincipalName: string, businessUnitId: string, roles: string[]) {
    return this.http.post(`${this.baseUrl}/roles`, {
      userPrincipalName,
      businessUnitId,
      roles,
    });
  }
}
